/* Dundon homepage hero (sections/dm-home-hero.liquid).
   - Video: the still is always painted first. A video starts once that still
     has loaded and painted (or at window load, whichever comes first), while
     the hero is at least a quarter on screen and the tab is visible, and never
     under reduced motion or Data Saver (a Play button is offered instead).
     Waiting for window load alone meant waiting for every ad and analytics tag,
     6 to 10 s on a first visit; the still is the LCP either way, because a
     later video frame of the same size cannot replace it. Muted is set as a property before play(): Safari ignores
     the attribute alone. A refused play() leaves the still in place.
   - YouTube: a facade. The iframe is injected after window load, only for the
     active visible slide, and not at all when Shopify's privacy API says the
     visitor is in a consent region and has not allowed marketing.
   - Slider: a native scroll-snap track, so swipe works without this file.
     Arrows, dots and keys call scrollTo; the active slide is read back from the
     scroll position. Only the active slide's video plays. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };
  var saveData = Boolean(navigator.connection && navigator.connection.saveData);
  var pageLoaded = document.readyState === 'complete';
  var onLoad = [];

  if (!pageLoaded) {
    window.addEventListener('load', function () {
      pageLoaded = true;
      onLoad.splice(0).forEach(function (fn) { fn(); });
    });
  }

  function afterLoad(fn) {
    if (pageLoaded) fn();
    else onLoad.push(fn);
  }

  function track(name, params) {
    if (typeof window.dmTrack === 'function') window.dmTrack(name, params);
  }

  function motionAllowed() {
    return !reduceMotion.matches && !saveData;
  }

  function consentBlocksYouTube() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return false;
    try {
      var consentRegion = typeof privacy.shouldShowBanner === 'function' && privacy.shouldShowBanner();
      var allowed = typeof privacy.marketingAllowed !== 'function' || privacy.marketingAllowed();
      return Boolean(consentRegion && !allowed);
    } catch (error) {
      return false;
    }
  }

  function Hero(root) {
    this.root = root;
    this.track = root.querySelector('[data-dmh-track]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-dmh-slide]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-dmh-dot]'));
    this.counter = root.querySelector('[data-dmh-counter]');
    this.toggle = root.querySelector('[data-dmh-toggle]');
    this.index = 0;
    this.visible = false;
    this.userPlay = false;
    this.hoverPaused = false;
    this.userPaused = false;
    this.viewedIndex = -1;
    this.viewTimer = null;
    this.autoTimer = null;
    this.interval = Math.max(6, parseInt(root.getAttribute('data-interval'), 10) || 8) * 1000;
    this.autoplay = root.getAttribute('data-autoplay') === 'true';
    this.advanceOnEnd = root.getAttribute('data-advance-on-end') === 'true';
    if (this.slides.length) this.init();
  }

  Hero.prototype.init = function () {
    var self = this;

    this.slides.forEach(function (slide) {
      var video = slide.querySelector('[data-dmh-video]');
      var playButton = slide.querySelector('[data-dmh-play]');
      if (video) {
        video.muted = true;
        video.addEventListener('playing', function () { slide.classList.add('is-playing'); });
        video.addEventListener('ended', function () {
          if (self.advanceOnEnd && self.slides.length > 1) self.goTo(self.index + 1, true);
        });
      }
      if (playButton) {
        if (!motionAllowed()) playButton.hidden = false;
        playButton.addEventListener('click', function () {
          self.userPlay = true;
          playButton.hidden = true;
          self.sync();
        });
      }
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          self.visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        });
        self.sync();
        self.scheduleView();
        self.scheduleAutoplay();
      }, { threshold: [0, 0.25, 0.5] }).observe(this.root);
    } else {
      this.visible = true;
    }

    document.addEventListener('visibilitychange', function () {
      self.sync();
      self.scheduleAutoplay();
    });

    if (typeof reduceMotion.addEventListener === 'function') {
      reduceMotion.addEventListener('change', function () { self.sync(); });
    }

    this.root.addEventListener('click', function (event) {
      var cta = event.target.closest ? event.target.closest('[data-dmh-cta]') : null;
      if (!cta) return;
      track('hero_cta_click', {
        index: self.index,
        label: cta.getAttribute('data-label') || cta.textContent.trim(),
        href: cta.getAttribute('href')
      });
    });

    if (this.slides.length > 1) this.initSlider();

    this.stillPainted = pageLoaded;
    var still = this.slides[0] && this.slides[0].querySelector('.dmh-hero__still');
    var painted = function () {
      if (self.stillPainted) return;
      // Two frames: the still has been composited, not just decoded.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          self.stillPainted = true;
          self.sync();
        });
      });
    };
    if (still && still.complete && still.naturalWidth) painted();
    else if (still) still.addEventListener('load', painted);

    afterLoad(function () {
      self.stillPainted = true;
      self.sync();
    });
    this.sync();
  };

  Hero.prototype.canPlay = function (slide) {
    // A YouTube iframe pulls in about 1 MB of script, so it still waits for window load.
    var ready = slide && slide.querySelector('[data-dmh-youtube]') ? pageLoaded : this.stillPainted;
    return ready && this.visible && !document.hidden && (motionAllowed() || this.userPlay);
  };

  Hero.prototype.sync = function () {
    var self = this;
    this.slides.forEach(function (slide, i) {
      if (i === self.index && self.canPlay(slide)) self.start(slide);
      else self.stop(slide, i !== self.index);
    });
  };

  Hero.prototype.start = function (slide) {
    var video = slide.querySelector('[data-dmh-video]');
    if (video) {
      video.muted = true;
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function () { slide.classList.remove('is-playing'); });
      }
      return;
    }

    var holder = slide.querySelector('[data-dmh-youtube]');
    if (!holder || holder.firstChild || consentBlocksYouTube()) return;
    var id = holder.getAttribute('data-dmh-youtube');
    var iframe = document.createElement('iframe');
    iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
      '?autoplay=1&mute=1&controls=0&loop=1&playlist=' + encodeURIComponent(id) +
      '&playsinline=1&rel=0&modestbranding=1&iv_load_policy=3';
    iframe.title = holder.getAttribute('data-dmh-youtube-title') || 'Video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.setAttribute('tabindex', '-1');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.addEventListener('load', function () {
      // YouTube paints black before the first frame; keep the still until then.
      setTimeout(function () { slide.classList.add('is-playing'); }, 900);
    });
    holder.appendChild(iframe);
  };

  Hero.prototype.stop = function (slide, reset) {
    var video = slide.querySelector('[data-dmh-video]');
    if (video) {
      video.pause();
      if (reset) {
        slide.classList.remove('is-playing');
        try { video.currentTime = 0; } catch (error) { /* not seekable yet */ }
      }
      return;
    }
    var holder = slide.querySelector('[data-dmh-youtube]');
    if (holder && holder.firstChild) {
      holder.innerHTML = '';
      slide.classList.remove('is-playing');
    }
  };

  Hero.prototype.scheduleView = function () {
    var self = this;
    clearTimeout(this.viewTimer);
    if (!this.visible || this.viewedIndex === this.index) return;
    this.viewTimer = setTimeout(function () {
      self.viewedIndex = self.index;
      track('hero_slide_view', {
        index: self.index,
        title: self.slides[self.index].getAttribute('data-title') || ''
      });
    }, 1000);
  };

  /* --- Slider ------------------------------------------------------------ */

  Hero.prototype.initSlider = function () {
    var self = this;
    var frame = null;

    this.track.addEventListener('scroll', function () {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        var width = self.track.clientWidth || 1;
        var next = Math.round(self.track.scrollLeft / width);
        next = Math.max(0, Math.min(self.slides.length - 1, next));
        if (next !== self.index) self.setActive(next);
      });
    }, { passive: true });

    var prev = this.root.querySelector('[data-dmh-prev]');
    var nextButton = this.root.querySelector('[data-dmh-next]');
    if (prev) prev.addEventListener('click', function () { self.goTo(self.index - 1, true); });
    if (nextButton) nextButton.addEventListener('click', function () { self.goTo(self.index + 1, true); });

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        self.goTo(parseInt(dot.getAttribute('data-dmh-dot'), 10) || 0, true);
      });
    });

    this.track.addEventListener('keydown', function (event) {
      if (event.target !== self.track) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); self.goTo(self.index + 1, true); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); self.goTo(self.index - 1, true); }
    });

    window.addEventListener('resize', function () {
      self.track.scrollLeft = self.index * self.track.clientWidth;
    });

    if (this.toggle) {
      this.toggle.addEventListener('click', function () {
        self.userPaused = !self.userPaused;
        self.toggle.setAttribute('aria-pressed', self.userPaused ? 'true' : 'false');
        self.toggle.setAttribute('aria-label', self.userPaused
          ? self.toggle.getAttribute('data-label-play')
          : self.toggle.getAttribute('data-label-pause'));
        self.root.classList.toggle('is-autoplay-paused', self.userPaused);
        self.scheduleAutoplay();
      });
    }

    // Autoplay yields to the visitor: hover and focus pause it, a touch stops it.
    this.root.addEventListener('mouseenter', function () { self.hoverPaused = true; self.scheduleAutoplay(); });
    this.root.addEventListener('mouseleave', function () { self.hoverPaused = false; self.scheduleAutoplay(); });
    this.root.addEventListener('focusin', function () { self.hoverPaused = true; self.scheduleAutoplay(); });
    this.root.addEventListener('focusout', function () { self.hoverPaused = false; self.scheduleAutoplay(); });
    this.root.addEventListener('touchstart', function () { self.autoplay = false; self.scheduleAutoplay(); }, { passive: true });

    this.setActive(0);
  };

  Hero.prototype.setActive = function (index) {
    var self = this;
    this.index = index;
    var supportsInert = 'inert' in HTMLElement.prototype;
    this.slides.forEach(function (slide, i) {
      var active = i === index;
      slide.classList.toggle('is-active', active);
      if (supportsInert) slide.inert = !active;
    });
    this.dots.forEach(function (dot, i) {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (this.counter) this.counter.textContent = String(index + 1);
    this.sync();
    this.scheduleView();
    this.scheduleAutoplay();
  };

  Hero.prototype.goTo = function (index, smooth) {
    var count = this.slides.length;
    var target = ((index % count) + count) % count;
    this.track.scrollTo({
      left: target * this.track.clientWidth,
      behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto'
    });
  };

  Hero.prototype.scheduleAutoplay = function () {
    var self = this;
    clearTimeout(this.autoTimer);
    var running = this.autoplay && this.slides.length > 1 && !reduceMotion.matches &&
      !this.userPaused && !this.hoverPaused && this.visible && !document.hidden;
    if (!running) return;
    this.autoTimer = setTimeout(function () { self.goTo(self.index + 1, true); }, this.interval);
  };

  /* --- Boot ------------------------------------------------------------- */

  function boot(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-dmh-hero]'), function (root) {
      if (!root.__dmhHero) root.__dmhHero = new Hero(root);
    });
  }

  boot(document);

  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target.closest ? event.target.closest('[data-dmh-slide]') : null;
    var root = slide && slide.closest('[data-dmh-hero]');
    if (!root || !root.__dmhHero || root.__dmhHero.slides.length < 2) return;
    root.__dmhHero.goTo(parseInt(slide.getAttribute('data-index'), 10) || 0, false);
  });
})();
